import BillingOverview from "./BillingOverview";
import { reconcileGambitUsage } from "../api/gambitWorkspace";
import { update_tokens } from "../store/appSlice";
import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Tooltip as MuiTooltip,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import {
  formatTokens,
  formatUsageDay,
  spendView,
  usageWindow,
} from "./usageModel";
import Refresh from "@mui/icons-material/Refresh";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useDashboardHeader } from "../contexts/DashboardShellContext";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { useBillingCache } from "../hooks/useBillingCache";
import {
  loadTokenBalance,
  TokenBalance,
  loadUsage,
  mergeUsage,
  UsagePage as Page,
  UsageSource,
  usageApiUrl,
} from "../api/usage";
import {
  storyToolPageContainerSx,
  storyToolMediaContentSx,
} from "./common/SettingsBackButton";
import { storyLibraryViewToggleSx } from "./common/StoryLibraryControls";
import PillMenu from "./common/PillMenu";
import UsageActivityTable, { ACTIVITY_PAGE_SIZE } from "./UsageActivityTable";
import { usageFilterPillSx } from "./UsageFiltersMenu";
import UsageProductSelect from './common/UsageProductSelect';
import HorizontalOverflowScroller from './common/HorizontalOverflowScroller';
import { UsageOverviewLoadingShell, UsageSkeleton } from "./common/UsageLoadingShell";
import CreationPricingSection, { CreationPricingToggle } from "./CreationPricingSection";
import {
  APP_ACCENT_GOLD,
  APP_BORDER,
  APP_TEXT_SECONDARY,
} from "./createShared";

const cardSx = {
  p: 2.5,
  border: `1px solid ${APP_BORDER}`,
  borderRadius: "16px",
  bgcolor: "#1B1E28",
  minWidth: 0,
};
export default function UsagePage() {
  const pricingId = useId();
  const compactToolbar = useMediaQuery(useTheme().breakpoints.down("md"));
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const userId = useAppSelector((state) => state.app.userId);
  const accountTokens = useAppSelector((state) => state.app.tokens);
  const [source, setSource] = useState<UsageSource>(
    window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1"
      ? "gambit"
      : "classic",
  );
  const [days, setDays] = useState(7);
  const [range, setRange] = useState(() => usageWindow(7));
  const cacheOwner = !authLoading && isAuthenticated ? userId || undefined : undefined;
  const { data: wallet, beginRequest: beginWalletRequest } = useBillingCache<TokenBalance>(cacheOwner, usageApiUrl('/usage/balance'));
  const [walletError, setWalletError] = useState("");
  const [reconciling, setReconciling] = useState(false);
  const [reconciliationError, setReconciliationError] = useState("");
  const reconciliation = useRef<AbortController | undefined>(undefined);
  const filters = useRef({ source, days });
  filters.current = { source, days };
  const historyKey = JSON.stringify([usageApiUrl('/usage'), source, range.from, range.to]);
  const { data: page, beginRequest: beginHistoryRequest } = useBillingCache<Page>(cacheOwner, historyKey);
  const pageRef = useRef(page);
  pageRef.current = page;
  const activityOrder = useRef<string[]>([]);
  const requestedView = searchParams.get("view");
  const view = requestedView === "models" || requestedView === "pricing" ? "pricing" : requestedView === "activity" ? "activity" : "overview";
  const setView = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === "overview") next.delete("view");
    else next.set("view", value);
    setSearchParams(next, { replace: true });
  };
  const [activityPage, setActivityPage] = useState(0);
  const retryRequest = useRef<{ cursor?: string; targetPage: number } | undefined>(undefined);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const pending = useRef<AbortController | undefined>(undefined);
  const pendingWallet = useRef<AbortController | undefined>(undefined);
  useDashboardHeader(
    {
      title: "Usage",
      back: { label: "Back to account", onClick: () => navigate("/settings") },
    },
    [navigate],
  );
  const refresh = useCallback(
    async (cursor?: string, targetPage = 0) => {
      pending.current?.abort();
      const controller = new AbortController();
      pending.current = controller;
      setLoading(true);
      setError("");
      const saveHistory = beginHistoryRequest();
      retryRequest.current = { cursor, targetPage };
      try {
        let requestCursor = cursor;
        const visited = new Set<string>();
        let combined = cursor ? pageRef.current : undefined;
        do {
          if (requestCursor) visited.add(requestCursor);
          const next = await loadUsage(source, range, requestCursor, controller.signal);
          if (controller.signal.aborted) return;
          if (next.nextCursor && visited.has(next.nextCursor))
            throw new Error("History did not advance. Refresh usage before loading more.");
          combined = { ...next, entries: mergeUsage(combined?.entries || [], next.entries) };
          requestCursor = combined.nextCursor;
          // A record page can contain only refunds, grants or already grouped stories.
          // Fill the requested visible page so later fetches cannot skip unseen rows.
        } while (requestCursor && !(source === "gambit" && combined.spend)
          && spendView(combined, source, range).activity.length < (targetPage + 1) * ACTIVITY_PAGE_SIZE);
        // Swap a complete visible page only after its refresh succeeds. Cached
        // rows stay on screen even if a later request in this batch fails.
        if (!cursor) activityOrder.current = [];
        const known = new Set(activityOrder.current);
        for (const row of spendView(combined, source, range).activity) {
          if (!known.has(row.id)) activityOrder.current.push(row.id);
        }
        pageRef.current = combined;
        saveHistory(combined);
        const rowCount = spendView(combined, source, range).activity.length;
        setActivityPage(Math.min(targetPage, Math.max(0, Math.ceil(rowCount / ACTIVITY_PAGE_SIZE) - 1)));
        retryRequest.current = undefined;
      } catch (failure) {
        if (!controller.signal.aborted)
          setError(
            failure instanceof Error && failure.name !== "TypeError"
              ? failure.message
              : "Could not load usage. Please try again.",
          );
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    },
    [source, range, beginHistoryRequest],
  );
  const expectedTokens = useRef(accountTokens);
  expectedTokens.current = accountTokens;
  const refreshWallet = useCallback(
    async (signal?: AbortSignal, force = false) => {
      pendingWallet.current?.abort();
      const controller = new AbortController();
      pendingWallet.current = controller;
      const abort = () => controller.abort();
      if (signal?.aborted) abort();
      signal?.addEventListener('abort', abort, { once: true });
      const saveWallet = beginWalletRequest();
      try {
        const next = await loadTokenBalance(controller.signal, { force, expectedTotal: expectedTokens.current });
        if (!controller.signal.aborted) {
          saveWallet(next);
          setWalletError("");
          if (next.available && Number.isSafeInteger(next.total))
            dispatch(update_tokens({ tokens: next.total! }));
        }
      } catch {
        if (!controller.signal.aborted)
          setWalletError(
            "Could not load token balances. Please refresh to retry.",
          );
      } finally {
        signal?.removeEventListener('abort', abort);
      }
    },
    [dispatch, beginWalletRequest],
  );
  useEffect(() => () => reconciliation.current?.abort(), []);
  const refreshAll = async () => {
    if (!userId) return;
    const controller = new AbortController();
    reconciliation.current?.abort();
    reconciliation.current = controller;
    setReconciling(true);
    setReconciliationError("");
    try {
      if (source === "gambit") {
        const result = await reconcileGambitUsage(userId, controller.signal);
        if (!controller.signal.aborted && result?.failed)
          setReconciliationError("Some pending usage could not be confirmed yet. Please refresh again shortly.");
      }
    } catch {
      if (!controller.signal.aborted)
        setReconciliationError("Pending usage could not be checked. Your recorded usage is still available.");
    } finally {
      if (!controller.signal.aborted) {
        if (filters.current.source === source && filters.current.days === days) setRange(usageWindow(days));
        await refreshWallet(controller.signal, true);
        if (!controller.signal.aborted) setReconciling(false);
      }
    }
  };
  useEffect(() => {
    const controller = new AbortController();
    setWalletError('');
    if (authLoading || !isAuthenticated || !userId) return;
    const refreshOnFocus = () => void refreshWallet(controller.signal);
    refreshOnFocus();
    window.addEventListener("focus", refreshOnFocus);
    return () => {
      controller.abort();
      pendingWallet.current?.abort();
      window.removeEventListener("focus", refreshOnFocus);
    };
  }, [authLoading, isAuthenticated, userId, refreshWallet]);
  const lastAccountBalance = useRef({ owner: cacheOwner, tokens: accountTokens });
  useEffect(() => {
    const previous = lastAccountBalance.current;
    lastAccountBalance.current = { owner: cacheOwner, tokens: accountTokens };
    if (!cacheOwner || previous.owner !== cacheOwner || previous.tokens === accountTokens || wallet?.total === accountTokens) return;
    const controller = new AbortController();
    void refreshWallet(controller.signal, true);
    return () => controller.abort();
  }, [cacheOwner, accountTokens, wallet?.total, refreshWallet]);
  useEffect(() => {
    if (!authLoading && !isAuthenticated)
      navigate("/signin?returnTo=%2Fusage", { replace: true });
    if (!authLoading && isAuthenticated && userId) void refresh();
    setActivityPage(0);
    activityOrder.current = [];
    return () => pending.current?.abort();
  }, [authLoading, isAuthenticated, userId, navigate, refresh]);
  const spending = useMemo(() => {
    const result = spendView(page, source, range);
    const order = new Map(activityOrder.current.map((id, index) => [id, index]));
    // Older records may change a story total; keep already paged rows in place.
    result.activity.sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
    return result;
  }, [page, source, range]);
  const hasMoreActivity = !!page?.nextCursor && !(source === "gambit" && page.spend);
  const nextActivityPage = () => {
    const next = activityPage + 1;
    if ((next + 1) * ACTIVITY_PAGE_SIZE <= spending.activity.length || !hasMoreActivity) setActivityPage(next);
    else void refresh(page?.nextCursor, next);
  };
  const waitingForAuth = authLoading || !isAuthenticated || !userId;
  const initialLoading = waitingForAuth || (loading && !page);
  return (
    <Box
      sx={{ ...storyToolPageContainerSx, color: "#FFFFFF", textAlign: "left" }}
    >
      <Box
        sx={{
          ...storyToolMediaContentSx,
          maxWidth: 1100,
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <BillingOverview wallet={wallet} loading={waitingForAuth || (!wallet && !walletError)} />
        {view !== "pricing" && walletError && <Alert severity="warning">{walletError}</Alert>}
        {view !== "pricing" && reconciliationError && <Alert severity="warning">{reconciliationError}</Alert>}
        <HorizontalOverflowScroller
          ariaLabel="Usage filters"
          fadeWidth={32}
          contentSx={{
            justifyContent: 'space-between',
            gap: 1,
            py: 0.25,
            minHeight: { xs: 44, md: 58 },
            '& > *': { flexShrink: 0 },
          }}
        >
          {compactToolbar ? <PillMenu label="Usage view" value={view} sx={usageFilterPillSx} options={[
            { value: "overview", label: "Overview" }, { value: "activity", label: "Activity" }, { value: "pricing", label: "Pricing" },
          ]} onChange={setView} /> : <Tabs value={view} onChange={(_, value) => setView(value)} aria-label="Usage views" variant="scrollable" scrollButtons="auto"
            sx={{ minHeight: 44, "& .MuiTabs-indicator": { bgcolor: APP_ACCENT_GOLD },
              "& .MuiTab-root": { minHeight: 44, color: APP_TEXT_SECONDARY, fontSize: 14, fontWeight: 700, textTransform: "none" },
              "& .Mui-selected": { color: "white !important" } }}>
            <Tab value="overview" label="Overview" id="usage-overview-tab" aria-controls="usage-overview-panel" />
            <Tab value="activity" label="Activity" id="usage-activity-tab" aria-controls="usage-activity-panel" />
            <Tab value="pricing" label="Pricing" id="usage-pricing-tab" aria-controls="usage-pricing-panel" />
          </Tabs>}
          {view === "pricing" ? <CreationPricingToggle id={pricingId} /> : <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: "auto" }}>
            <UsageProductSelect value={source} onChange={setSource} />
            <PillMenu label="Period" mobileSheet value={days} sx={usageFilterPillSx} options={[{ value: 7, label: "7 days" }, { value: 30, label: "30 days" }]}
              onChange={value => { setDays(value); setRange(usageWindow(value)); }} />
            {!compactToolbar && <MuiTooltip title="Refresh usage"><span><IconButton aria-label="Refresh usage" disabled={loading || reconciling}
              onClick={() => void refreshAll()} sx={storyLibraryViewToggleSx(false)}><Refresh /></IconButton></span></MuiTooltip>}
          </Stack>}
        </HorizontalOverflowScroller>
        <Box role={compactToolbar ? "region" : "tabpanel"} id="usage-overview-panel" aria-label={compactToolbar ? "Overview" : undefined} aria-labelledby={compactToolbar ? undefined : "usage-overview-tab"} hidden={view !== "overview"}>
        {view === "overview" && initialLoading ? <Box sx={cardSx}><UsageOverviewLoadingShell /></Box> : page?.available && view === "overview" && (
          <Box sx={cardSx}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
            >
              <Box>
                <Typography color={APP_TEXT_SECONDARY}>
                  Fable tokens spent
                </Typography>
                <Typography
                  component="p"
                  sx={{
                    fontSize: 40,
                    fontWeight: 800,
                    lineHeight: 1.3,
                    mt: 0.5,
                  }}
                >
                  {spending.unpriced && !spending.total
                    ? "—"
                    : formatTokens(spending.total)}
                </Typography>
                <Typography color={APP_TEXT_SECONDARY} fontSize={13}>
                  {formatUsageDay(range.from.slice(0, 10))} –{" "}
                  {formatUsageDay(spending.days[spending.days.length - 1].day)}
                  {" · UTC"}
                  {spending.refunded > 0
                    ? ` · ${formatTokens(spending.refunded)} tokens refunded`
                    : ""}
                </Typography>
              </Box>

            </Stack>
            <Box
              role="img"
              aria-label={`Daily Fable-token spending: ${spending.days.map((day) => `${formatUsageDay(day.day)}: ${formatTokens(day.tokens)}`).join("; ")}`}
              sx={{ height: { xs: 180, sm: 220 }, mt: 3, minWidth: 0 }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={spending.days}
                  margin={{ top: 8, right: 4, left: -16, bottom: 0 }}
                  accessibilityLayer
                >
                  <CartesianGrid vertical={false} stroke={APP_BORDER} />
                  <XAxis
                    dataKey="day"
                    tickFormatter={formatUsageDay}
                    axisLine={false}
                    tickLine={false}
                    stroke={APP_TEXT_SECONDARY}
                    minTickGap={42}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis
                    tickFormatter={formatTokens}
                    axisLine={false}
                    tickLine={false}
                    stroke={APP_TEXT_SECONDARY}
                    tick={{ fontSize: 12 }}
                    width={52}
                  />
                  <Tooltip
                    cursor={{ fill: "rgba(255,255,255,0.03)" }}
                    content={({ active, payload, label }) =>
                      active && payload?.length ? (
                        <Box sx={{ ...cardSx, p: 1.5 }}>
                          <Typography fontSize={13} color={APP_TEXT_SECONDARY}>
                            {formatUsageDay(String(label))}
                          </Typography>
                          <Typography fontWeight={700}>
                            {formatTokens(Number(payload[0].value))} Fable
                            tokens
                          </Typography>
                        </Box>
                      ) : null
                    }
                  />
                  <Bar
                    dataKey="tokens"
                    fill={APP_ACCENT_GOLD}
                    radius={[5, 5, 0, 0]}
                    maxBarSize={44}
                    isAnimationActive={false}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
            {spending.partial && (
              <Typography
                fontSize={13}
                color={APP_TEXT_SECONDARY}
                sx={{ mt: 1 }}
              >
                Based on loaded activity. Use the next-page arrow in the Activity tab to include older recorded spending.
              </Typography>
            )}
          </Box>
        )}
        </Box>
        {view !== "pricing" && error && (
          <Alert
            severity="warning"
            action={
              <Button
                color="inherit"
                onClick={() => {
                  void refresh(retryRequest.current?.cursor, retryRequest.current?.targetPage);
                  void refreshWallet(undefined, true);
                }}
              >
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}
        {view !== "pricing" && page?.available === false && (
          <Box sx={cardSx}>
            <Typography>{page.reason}</Typography>
          </Box>
        )}
        <Box role={compactToolbar ? "region" : "tabpanel"} id="usage-activity-panel" aria-label={compactToolbar ? "Activity" : undefined} aria-labelledby={compactToolbar ? undefined : "usage-activity-tab"} hidden={view !== "activity"}>
          {view === "activity" && (initialLoading || page?.available) && <>
            <Stack direction="row" justifyContent="flex-end" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
              {initialLoading ? <UsageSkeleton width={150} height={19.5} /> : <Typography fontSize={13} color={APP_TEXT_SECONDARY}>{formatUsageDay(range.from.slice(0, 10))} – {formatUsageDay(spending.days[spending.days.length - 1].day)} · UTC</Typography>}
            </Stack>
            <UsageActivityTable rows={spending.activity} pageIndex={activityPage} hasMore={hasMoreActivity}
              loading={loading || waitingForAuth} initialLoading={initialLoading} classic={source === "classic"} onPrevious={() => setActivityPage(value => Math.max(0, value - 1))} onNext={nextActivityPage} />
          </>}
        </Box>
        <Box role={compactToolbar ? "region" : "tabpanel"} id="usage-pricing-panel" aria-label={compactToolbar ? "Pricing" : undefined} aria-labelledby={compactToolbar ? undefined : "usage-pricing-tab"} hidden={view !== "pricing"}>
          {view === "pricing" && <CreationPricingSection id={pricingId} showToggle={false} />}
        </Box>
      </Box>
    </Box>
  );
}
